import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "../components/Navbar";
import HelpCenter from "../components/HelpCenter";
import { trackEvent } from "../../lib/analytics";

const PLAN_DEFINITIONS = [
  {
    id: "free",
    highlight: false,
    songLimit: 20,
    features: [
      ["maxSongs", false],
      ["publicPage", false],
      ["qrAndLink", false],
      ["basicLibrary", false],
      ["manualSongAdd", false],
      ["customRequests", false],
      ["paidBoost", false],
      ["searchAndLanguageFilter", false],
      ["raffle", false],
      ["noWhatsapp", true],
      ["noSocialLinks", true],
      ["noHeroBranding", true],
      ["noSongHiding", true],
      ["noBulkVisibility", true],
      ["noSeoFaq", true],
      ["noEvents", true],
      ["noImports", true],
      ["noSetlists", true],
      ["noReaderPrompter", true],
      ["noGigNotes", true],
      ["noReportsSponsors", true],
    ],
  },
  {
    id: "basic",
    highlight: false,
    songLimit: 100,
    features: [
      ["maxSongs", false],
      ["trialFeatures", false],
      ["paidBoost", false],
      ["publicPageBasics", false],
      ["heroIntro", false],
      ["socialWebsiteWhatsapp", false],
      ["songVisibility", false],
      ["visibilityFilter", false],
      ["bulkLibraryActions", false],
      ["seo", false],
      ["faq", false],
      ["promoWhenClosed", false],
      ["noImports", true],
      ["noChordProImport", true],
      ["noSetlists", true],
      ["noReaderPrompter", true],
      ["noGigNotes", true],
      ["noReportsSponsors", true],
    ],
  },
  {
    id: "plus",
    highlight: true,
    songLimit: 500,
    features: [
      ["maxSongs", false],
      ["basicFeatures", false],
      ["paidBoost", false],
      ["events", false],
      ["eventRequestLimit", false],
      ["publicEventSelection", false],
      ["pdfDocxImport", false],
      ["chordProImport", false],
      ["driveImport", false],
      ["bulkImport", false],
      ["setlistGenerator", false],
      ["savedSetlists", false],
      ["gigSetlistView", false],
      ["readerPrompter", false],
      ["readerThemes", false],
      ["printPdf", false],
      ["gigNotes", false],
      ["reportsStats", false],
      ["sponsors", false],
    ],
  },
];

function getPlans(t) {
  return PLAN_DEFINITIONS.map((definition) => ({
    ...definition,
    name: t(`subscriptionPage.plans.${definition.id}.name`),
    eyebrow: t(`subscriptionPage.plans.${definition.id}.eyebrow`),
    price: t(`subscriptionPage.plans.${definition.id}.price`),
    description: t(`subscriptionPage.plans.${definition.id}.description`),
    features: definition.features.map(([key, negative]) => ({
      key,
      negative,
      text: t(`subscriptionPage.plans.${definition.id}.features.${key}`, {
        count: definition.songLimit,
      }),
    })),
  }));
}

function getCurrentPerformer() {
  try {
    const rawPerformer = window.sessionStorage.getItem(
      "paranoidi_auth_performer"
    );

    return rawPerformer ? JSON.parse(rawPerformer) : null;
  } catch {
    return null;
  }
}

function getPlanVisuals(planId) {
  if (planId === "plus") {
    return {
      card:
        "border-red-400/60 bg-gradient-to-b from-red-500/20 via-zinc-950 to-black shadow-[0_0_90px_rgba(239,68,68,0.22)]",
      badge: "bg-red-500 text-white",
      button: "bg-red-500 text-white hover:bg-red-400",
      glow: "bg-red-500/25",
      check: "text-red-300",
    };
  }

  if (planId === "basic") {
    return {
      card:
        "border-white/10 bg-gradient-to-b from-zinc-900 via-zinc-950 to-black",
      badge: "bg-zinc-800 text-zinc-200",
      button: "bg-zinc-100 text-black hover:bg-white",
      glow: "bg-zinc-400/10",
      check: "text-red-400",
    };
  }

  return {
    card:
      "border-white/10 bg-gradient-to-b from-black via-zinc-950 to-black",
    badge: "bg-white/[0.08] text-zinc-300",
    button: "bg-white/[0.08] text-white hover:bg-white/12",
    glow: "bg-red-500/10",
    check: "text-red-400",
  };
}

function getApiBaseUrl() {
  const fromWindow =
    typeof window !== "undefined" ? window.PARANOIDI_API_BASE_URL : "";

  return String(
    fromWindow ||
      process.env.REACT_APP_API_BASE_URL ||
      "http://localhost:4000/api"
  ).replace(/\/+$/, "");
}

export default function SubscriptionPage({ config }) {
  const { t } = useTranslation("admin");
  const [performer, setPerformer] = useState(null);
  const [checkoutPlanId, setCheckoutPlanId] = useState("");
  const [checkoutError, setCheckoutError] = useState("");
  const [subscriptionNotice, setSubscriptionNotice] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cameFromCheckoutSuccess = params.get("success") === "1";
    const cameFromPortal = params.get("portal") === "1";
    const cameFromCancel = params.get("canceled") === "1";

    // Stripe Checkout palauttaa onnistuneen maksun session tunnisteen URL:ssa.
    const checkoutSessionId = String(
      params.get("session_id") || ""
    ).trim();

    if (cameFromCancel) {
      setSubscriptionNotice({
        type: "info",
        titleKey: "subscriptionPage.notices.notChangedTitle",
        textKey: "subscriptionPage.notices.notChangedText",
      });
    }

    if (cameFromCheckoutSuccess || cameFromPortal) {
      setSubscriptionNotice({
        type: "success",
        titleKey: "subscriptionPage.notices.updatingTitle",
        textKey: "subscriptionPage.notices.updatingText",
      });
    }

    const currentPerformer = getCurrentPerformer();
    setPerformer(currentPerformer);

    if (!currentPerformer?.slug) {
      return;
    }

    /*
     * Stripe Checkoutista palattaessa tarkistetaan maksu vielä backendin kautta.
     * GA4 purchase lähetetään vain, jos Stripe vahvistaa session maksetuksi.
     */
    if (cameFromCheckoutSuccess && checkoutSessionId) {
      fetch(`${getApiBaseUrl()}/billing/checkout-status`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          slug: currentPerformer.slug,
          session_id: checkoutSessionId,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          if (
            !data?.ok ||
            !data?.paid ||
            !data?.transactionId ||
            !data?.currency
          ) {
            return;
          }

          const purchaseValue = Number(data.value || 0);
          const planName =
            data.plan === "plus"
              ? "Paranoidi Plus"
              : "Paranoidi Basic";

          trackEvent("purchase", {
            transaction_id: data.transactionId,
            value: purchaseValue,
            currency: data.currency,
            items: [
              {
                item_id: `paranoidi_${data.plan}`,
                item_name: planName,
                price: purchaseValue,
                quantity: 1,
              },
            ],
          });

          /*
           * Poistetaan Checkout-parametrit osoitteesta onnistuneen
           * käsittelyn jälkeen, jotta sivun päivitys ei käynnistä
           * samaa tarkistusta uudelleen.
           */
          const cleanUrl = new URL(window.location.href);
          cleanUrl.searchParams.delete("success");
          cleanUrl.searchParams.delete("plan");
          cleanUrl.searchParams.delete("session_id");

          window.history.replaceState(
            {},
            "",
            `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`
          );
        })
        .catch((error) => {
          console.error(
            "Stripe Checkout purchase tracking failed:",
            error
          );
        });
    }

    fetch(
      `${getApiBaseUrl()}/public/performers/${encodeURIComponent(
        currentPerformer.slug
      )}`
    )
      .then((response) => response.json())
      .then((data) => {
        if (!data?.ok || !data?.performer) {
          return;
        }

        const updatedPerformer = {
          ...currentPerformer,
          ...data.performer,
          features: data.performer.features,
          plan: data.performer.plan,
          subscriptionStatus: data.performer.subscriptionStatus,
          subscription_status: data.performer.subscription_status,
          trialEndsAt: data.performer.trialEndsAt,
          trial_ends_at: data.performer.trial_ends_at,
        };

        window.sessionStorage.setItem(
          "paranoidi_auth_performer",
          JSON.stringify(updatedPerformer)
        );

        setPerformer(updatedPerformer);

        if (cameFromCheckoutSuccess || cameFromPortal) {
          setSubscriptionNotice({
            type: "success",
            titleKey: "subscriptionPage.notices.updatedTitle",
            textKey: "subscriptionPage.notices.updatedText",
            planId: String(
              updatedPerformer.plan || "free"
            ).toLowerCase(),
          });
        }
      })
      .catch(() => {
        if (cameFromCheckoutSuccess || cameFromPortal) {
          setSubscriptionNotice({
            type: "info",
            titleKey: "subscriptionPage.notices.returnedTitle",
            textKey: "subscriptionPage.notices.returnedText",
          });
        }
      });
  }, []);

  useEffect(() => {
    const previousBodyOverflowX = document.body.style.overflowX;
    const previousHtmlOverflowX =
      document.documentElement.style.overflowX;

    document.body.style.overflowX = "hidden";
    document.documentElement.style.overflowX = "hidden";

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });

    return () => {
      document.body.style.overflowX = previousBodyOverflowX;
      document.documentElement.style.overflowX =
        previousHtmlOverflowX;
    };
  }, []);

  function handleLogout() {
    window.sessionStorage.removeItem("paranoidi_logged_in");
    window.sessionStorage.removeItem("paranoidi_auth_performer");
    window.sessionStorage.removeItem("paranoidi_auth_user");
    window.location.href = "/";
  }

  async function handleStartCheckout(planId) {
    const activePerformer = performer || getCurrentPerformer();
    const slug = activePerformer?.slug;
    const activePlan = String(
      activePerformer?.plan || currentPlan || "free"
    ).toLowerCase();

    const activeStatus = String(
      activePerformer?.subscriptionStatus ||
        activePerformer?.subscription_status ||
        ""
    ).toLowerCase();

    if (!slug) {
      setCheckoutError(
        t("subscriptionPage.errors.performerMissing")
      );
      return;
    }

    if (!["basic", "plus"].includes(planId)) {
      return;
    }

    const hasPaidStripeSubscription =
      ["basic", "plus"].includes(activePlan) &&
      activeStatus === "active";

    const checkoutFallbackErrorMessage =
      hasPaidStripeSubscription
        ? t("subscriptionPage.errors.portalOpenFailed")
        : t("subscriptionPage.errors.checkoutStartFailed");

    setCheckoutError("");
    setCheckoutPlanId(planId);

    try {
      const endpoint = hasPaidStripeSubscription
        ? "/billing/create-portal-session"
        : "/billing/create-checkout-session";

      const response = await fetch(
        `${getApiBaseUrl()}${endpoint}`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(
            hasPaidStripeSubscription
              ? { slug }
              : {
                  slug,
                  plan: planId,
                }
          ),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data?.url) {
        const requestError = new Error(
          checkoutFallbackErrorMessage
        );

        requestError.code = data?.code || "";
        requestError.payload = data || {};
        requestError.backendMessage = String(
          data?.error || ""
        ).trim();

        throw requestError;
      }

      window.location.href = data.url;
    } catch (error) {
      setCheckoutError(checkoutFallbackErrorMessage);
      setCheckoutPlanId("");
    }
  }

  const plans = getPlans(t);
  const currentPlan = String(
    performer?.plan || "free"
  ).toLowerCase();

  const currentFeatures = performer?.features || null;

  const currentPlanDefinition = plans.find(
    (plan) => plan.id === currentPlan
  );

  const currentPlanName =
    currentPlanDefinition?.name ||
    currentFeatures?.name ||
    currentPlan.charAt(0).toUpperCase() +
      currentPlan.slice(1);

  const noticePlanName = subscriptionNotice?.planId
    ? plans.find(
        (plan) => plan.id === subscriptionNotice.planId
      )?.name || subscriptionNotice.planId
    : "";

  return (
    <>
      <Navbar config={config} onLogout={handleLogout} />
      <HelpCenter />

      <main className="subscription-page relative isolate min-h-screen w-full max-w-full overflow-x-hidden bg-black px-3 pb-6 pt-28 text-white sm:px-6 sm:pt-32 lg:px-8">
        <div className="subscription-page-bg pointer-events-none absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute left-1/2 top-[-18rem] h-[34rem] w-[34rem] -translate-x-1/2 rounded-full bg-red-600/20 blur-3xl" />
          <div className="absolute bottom-[-10rem] right-[-6rem] h-[28rem] w-[28rem] rounded-full bg-red-900/20 blur-3xl" />
          <div className="absolute left-[-12rem] top-[26rem] h-[30rem] w-[30rem] rounded-full bg-red-800/10 blur-3xl" />
          <div className="absolute inset-0 opacity-[0.065] [background-image:linear-gradient(rgba(255,255,255,.8)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.8)_1px,transparent_1px)] [background-size:44px_44px]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.08),transparent_34%),linear-gradient(180deg,rgba(0,0,0,0),#000_88%)]" />
        </div>

        <div className="relative z-10 mx-auto w-full max-w-7xl min-w-0">
          <section className="subscription-hero overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.04] shadow-2xl">
            <div className="relative p-5 sm:p-8 lg:p-10">
              <div className="pointer-events-none absolute right-[-5rem] top-[-5rem] h-56 w-56 rounded-full bg-red-500/20 blur-3xl" />

              <div className="relative grid min-w-0 gap-6 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
                <div className="min-w-0">
                  <p className="text-xs font-black uppercase tracking-[0.28em] text-red-400">
                    {t("subscriptionPage.hero.eyebrow")}
                  </p>

                  <h1 className="mt-4 max-w-4xl text-4xl font-black uppercase leading-[0.92] tracking-[-0.06em] text-white sm:text-6xl">
                    {t("subscriptionPage.hero.title")}
                  </h1>

                  <p className="mt-5 max-w-2xl text-sm font-semibold leading-7 text-zinc-300 sm:text-base sm:leading-8">
                    {t(
                      "subscriptionPage.hero.description"
                    )}
                  </p>
                </div>

                <div className="subscription-current-plan min-w-0 rounded-[1.5rem] border border-red-400/20 bg-red-500/10 p-5 shadow-[0_0_60px_rgba(239,68,68,0.12)]">
                  <p className="text-xs font-black uppercase tracking-[0.22em] text-red-200">
                    {t(
                      "subscriptionPage.currentPlan.label"
                    )}
                  </p>

                  <h2 className="mt-2 break-words text-3xl font-black text-white">
                    {currentPlanName}
                  </h2>

                  <p className="mt-2 text-sm font-semibold leading-6 text-zinc-300">
                    {t(
                      "subscriptionPage.currentPlan.songLimit",
                      {
                        count:
                          currentFeatures?.songLimit ||
                          currentPlanDefinition?.songLimit ||
                          20,
                      }
                    )}
                  </p>
                </div>
              </div>

              {checkoutError && (
                <div className="relative mt-6 rounded-2xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm font-semibold leading-6 text-red-100">
                  {checkoutError}
                </div>
              )}

              {subscriptionNotice && (
                <div
                  className={[
                    "subscription-notice relative mt-6 rounded-2xl border px-4 py-4 text-sm font-semibold leading-6",
                    subscriptionNotice.type === "success"
                      ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-100"
                      : "border-white/15 bg-white/[0.06] text-zinc-100",
                  ].join(" ")}
                >
                  <p className="text-base font-black text-white">
                    {t(subscriptionNotice.titleKey)}
                  </p>

                  <p className="mt-1 text-sm font-semibold leading-6">
                    {t(subscriptionNotice.textKey, {
                      plan: noticePlanName,
                    })}
                  </p>

                  <a
                    href="/admin"
                    className="mt-4 inline-flex rounded-full bg-white px-5 py-3 text-xs font-black uppercase tracking-[0.12em] text-black transition hover:bg-zinc-100"
                  >
                    {t(
                      "subscriptionPage.notices.goToDashboard"
                    )}
                  </a>
                </div>
              )}
            </div>
          </section>

          <section className="mt-6 overflow-hidden rounded-[2rem] border border-red-400/30 bg-red-500/10 p-5 shadow-[0_0_70px_rgba(239,68,68,0.12)] sm:mt-8 sm:p-7">
            <div className="grid gap-5 lg:grid-cols-[1fr_auto] lg:items-center">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.24em] text-red-300">
                  {t("subscriptionPage.boost.eyebrow")}
                </p>

                <h2 className="mt-3 text-3xl font-black uppercase tracking-[-0.05em] text-white sm:text-4xl">
                  {t("subscriptionPage.boost.title")}
                </h2>

                <p className="mt-3 max-w-3xl text-sm font-semibold leading-7 text-zinc-300">
                  {t(
                    "subscriptionPage.boost.description"
                  )}
                </p>

                <p className="mt-2 text-xs font-semibold leading-6 text-zinc-400">
                  {t("subscriptionPage.boost.details")}
                </p>
              </div>

              <div className="flex flex-wrap gap-2 lg:max-w-[15rem] lg:justify-end">
                {["free", "basic", "plus"].map(
                  (planId) => {
                    const planName =
                      plans.find(
                        (plan) => plan.id === planId
                      )?.name || planId;

                    return (
                      <span
                        key={planId}
                        className="rounded-full border border-red-300/20 bg-black/30 px-4 py-2 text-xs font-black uppercase tracking-[0.12em] text-red-100"
                      >
                        {t(
                          "subscriptionPage.boost.availableBadge",
                          {
                            plan: planName,
                          }
                        )}
                      </span>
                    );
                  }
                )}
              </div>
            </div>
          </section>

          <section className="mt-6 grid w-full min-w-0 gap-4 sm:mt-8 sm:gap-5 md:grid-cols-3">
            {plans.map((plan) => {
              const isCurrent =
                plan.id === currentPlan;

              const visuals = getPlanVisuals(plan.id);

              const limitLabel = t(
                "subscriptionPage.planCard.songLimitValue",
                {
                  count: plan.songLimit,
                }
              );

              return (
                <article
                  key={plan.id}
                  className={[
                    `subscription-plan-card subscription-plan-${plan.id} relative flex min-w-0 overflow-hidden rounded-[2rem] border p-5 transition duration-300 hover:-translate-y-1 sm:p-6`,
                    "md:min-h-[34rem]",
                    visuals.card,
                    isCurrent
                      ? "ring-2 ring-red-400/70"
                      : "",
                  ].join(" ")}
                >
                  <div
                    className={[
                      "pointer-events-none absolute right-[-4rem] top-[-4rem] h-44 w-44 rounded-full blur-3xl",
                      visuals.glow,
                    ].join(" ")}
                  />

                  {plan.highlight && (
                    <div className="absolute right-5 top-5 rounded-full bg-red-500 px-3 py-1 text-[0.65rem] font-black uppercase tracking-[0.16em] text-white shadow-lg">
                      {t(
                        "subscriptionPage.planCard.mostPopular"
                      )}
                    </div>
                  )}

                  <div className="relative flex w-full min-w-0 flex-col">
                    <span
                      className={[
                        "subscription-plan-badge inline-flex w-fit max-w-full rounded-full px-3 py-1 text-[0.65rem] font-black uppercase tracking-[0.16em]",
                        visuals.badge,
                      ].join(" ")}
                    >
                      {plan.eyebrow}
                    </span>

                    <h2 className="mt-5 break-words text-3xl font-black text-white">
                      {plan.name}
                    </h2>

                    <p className="mt-2 text-sm font-semibold leading-6 text-zinc-400 md:min-h-[3rem]">
                      {plan.description}
                    </p>

                    <div className="mt-5">
                      <p className="text-4xl font-black tracking-[-0.04em] text-white">
                        {plan.price}
                      </p>

                      {plan.id !== "free" && (
                        <p className="mt-1 text-xs font-bold uppercase tracking-[0.16em] text-zinc-500">
                          {t(
                            "subscriptionPage.planCard.priceSuffix"
                          )}
                        </p>
                      )}
                    </div>

                    {limitLabel && (
                      <div className="subscription-plan-limit mt-5 min-w-0 rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3">
                        <p className="text-xs font-black uppercase tracking-[0.16em] text-zinc-500">
                          {t(
                            "subscriptionPage.planCard.songLimitLabel"
                          )}
                        </p>

                        <p className="mt-1 break-words text-lg font-black text-white">
                          {limitLabel}
                        </p>
                      </div>
                    )}

                    <ul className="mb-8 mt-5 grid gap-3 text-sm font-semibold leading-6 text-zinc-300">
                      {plan.features.map((feature) => {
                        const isNegative =
                          feature.negative;

                        return (
                          <li
                            key={feature.key}
                            className="flex min-w-0 gap-2"
                          >
                            <span
                              className={[
                                "shrink-0",
                                isNegative
                                  ? "text-zinc-600"
                                  : visuals.check,
                              ].join(" ")}
                            >
                              {isNegative ? "–" : "✓"}
                            </span>

                            <span
                              className={[
                                "min-w-0 break-words",
                                isNegative
                                  ? "text-zinc-500"
                                  : "",
                              ].join(" ")}
                            >
                              {feature.text}
                            </span>
                          </li>
                        );
                      })}
                    </ul>

                    <button
                      type="button"
                      disabled={
                        plan.id === "free" ||
                        Boolean(checkoutPlanId)
                      }
                      onClick={() =>
                        handleStartCheckout(plan.id)
                      }
                      className={[
                        "subscription-plan-button mt-7 w-full rounded-full px-5 py-3 text-sm font-black uppercase tracking-[0.12em] transition md:mt-auto",
                        plan.id === "free" ||
                        Boolean(checkoutPlanId)
                          ? "cursor-not-allowed bg-zinc-900 text-zinc-500"
                          : visuals.button,
                      ].join(" ")}
                    >
                      {plan.id === "free"
                        ? t(
                            "subscriptionPage.planCard.trialOnlyNew"
                          )
                        : checkoutPlanId === plan.id
                          ? t(
                              "subscriptionPage.planCard.opening"
                            )
                          : isCurrent
                            ? t(
                                "subscriptionPage.planCard.manageSubscription"
                              )
                            : ["basic", "plus"].includes(
                                  currentPlan
                                )
                              ? t(
                                  "subscriptionPage.planCard.changeInPortal"
                                )
                              : t(
                                  "subscriptionPage.planCard.startPlan",
                                  {
                                    plan: plan.name,
                                  }
                                )}
                    </button>
                  </div>
                </article>
              );
            })}
          </section>

          <section className="subscription-recommendation mt-6 rounded-[2rem] border border-white/10 bg-white/[0.04] p-5 sm:mt-8 sm:p-8">
            <div className="grid min-w-0 gap-5 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
              <div className="min-w-0">
                <p className="text-xs font-black uppercase tracking-[0.24em] text-red-400">
                  {t(
                    "subscriptionPage.recommendation.eyebrow"
                  )}
                </p>

                <h2 className="mt-3 text-3xl font-black uppercase tracking-[-0.05em] text-white sm:text-4xl">
                  {t(
                    "subscriptionPage.recommendation.title"
                  )}
                </h2>
              </div>

              <p className="text-sm font-semibold leading-7 text-zinc-300">
                {t(
                  "subscriptionPage.recommendation.description"
                )}
              </p>
            </div>
          </section>

          <div className="pb-4 pt-6 text-center text-xs font-semibold leading-5 text-zinc-600 sm:pb-2">
            {t("subscriptionPage.footer")}
          </div>
        </div>
      </main>
    </>
  );
}